import { combineLatest, forkJoin, of } from 'rxjs'
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators'

import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router'
import { AdminService, OeePageFlags } from '@services/admin.service'
import { PermissionService } from '@services/permission.service'
import { AuthenticationService, hasUserAdminAccess } from '@services/authentication.service'
import { SubscriptionService } from '@services/subscription.service'
import { ProjectService } from '@services/project.service'
import { UserService } from '@services/user.service'
import { pickDefaultOeeSubRoute } from 'src/app/utils/oeeDefaultRoute'

export const CustomAuthGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService)
  const router = inject(Router)

  return combineLatest([
    authService.isLoading$,
    authService.isAuthenticated$,
    authService.user$,
  ]).pipe(
    filter(([isLoading, authenticated, user]) => !isLoading && (!authenticated || user != null)),
    take(1),
    switchMap(([, authenticated, user]) => {
      /** Redirect to our login page if activating route while unauthenticated */
      if (!authenticated) {
        sessionStorage.setItem('authRedirectOrigin', state.url)
        router.navigate(['login'])
        return of(false)
      }

      if (user && !user.org_id) {
        return authService.getUserAccounts().pipe(
          catchError(() => of({ organizations: [], hasFreeTrial: false })),
          map(account => {
            if (account.organizations.length > 0) {
              authService.recordAuthDiagnosticEvent('no_org_session_redirected_to_login', {
                organizationCount: account.organizations.length,
                target: state.url,
              })
              sessionStorage.setItem('authRedirectOrigin', state.url)
              router.navigate(['login'])
              return false
            }

            return true
          }),
        )
      }

      return of(true)
    })
  )
}

/** Checks if the authenticated user's organization role is an Admin role */
export const isUserAdminGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService)
  const router = inject(Router)

  return authService.getCurrentUsersRoles$().pipe(
    map(roles => {
      if (hasUserAdminAccess(...roles)) {
        return true
      } else {
        router.navigate(['projects'])
        return false
      }
    })
  )
}

/** Checks if authenticated user is in free tier, has an admin role, or is a Kartorium super-admin.
 *  A missing/errored subscription still falls through to the role check so admins can reach
 *  /billing to fix a canceled/past_due/absent subscription. */
export const canAccessBillingGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService)
  const router = inject(Router)
  const subscriptionService = inject(SubscriptionService)
  const userService = inject(UserService)

  return subscriptionService.getSubscriptionDetails().pipe(
    catchError(() => of({ tier: null } as any)),
    switchMap(details => {
      if (details?.tier == 'free') {
        return of(true)
      } else {
        return forkJoin([
          authService.getCurrentUsersRoles$(),
          userService.getCurrentUser(),
        ]).pipe(
          map(([roles, user]) => {
            if (hasUserAdminAccess(...roles) || user?.superAdmin) {
              return true
            } else {
              router.navigate(['projects'])
              return false
            }
          })
        )
      }
    })
  )
}

export const hasSubscriptionGuard: CanActivateFn = (route, state) => {
  const subscriptionService = inject(SubscriptionService)
  const router = inject(Router)

  return subscriptionService.getSubscriptionDetails().pipe(
    catchError(((e) => {
      sessionStorage.setItem('authRedirectOrigin', state.url)
      router.navigate(['login'])
    }) as any),
    map(details => {
      if (details.status == 'active' || details.status == 'trialing') {
        return true
      } else {
        router.navigate(['error/402'])

        return false
      }
    })
  )
}

export const hasNoOrganizationGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService)
  const router = inject(Router)

  return authService.getUserAccounts().pipe(
    catchError(error => of({ organizations: [], hasFreeTrial: false })),
    map(account => {
      sessionStorage.setItem('authRedirectOrigin', state.url)
      if (account.organizations.length > 0) {
        router.navigate(['login'])
        return false
      } else if (account.hasFreeTrial) {
        router.navigate(['login'])
      }

      return true
    })
  )
}

export const hasNoSubscriptionGuard: CanActivateFn = (route, state) => {
  const subscriptionService = inject(SubscriptionService)
  const router = inject(Router)

  return subscriptionService.getSubscriptionDetails().pipe(
    catchError(error => of({ status: 'none' })),
    map(details => {
      if (details.status == 'active' || details.status == 'trialing') {
        router.navigate(['projects'])
        return false
      } else {
        return true
      }
    }),
  )
}


export const signupGuard: CanActivateFn = (route, state) => {
  const authenticationService = inject(AuthenticationService)

  return authenticationService.isAuthenticated$.pipe(
    tap(isAuthenticated => {
      if (!isAuthenticated) {
        authenticationService.loginWithRedirect({
          appState: { target: 'signup' },
          authorizationParams: { screen_hint: 'signup' }
        })
      }
    }),
    map(() => !authenticationService.hasOrganization)
  )
}

/** Checks if the authenticated user's subscription is valid */
export const subscriptionIsActiveGuard: CanActivateFn = (route, state) => {
  const authenticationService = inject(AuthenticationService)
  const subscriptionService = inject(SubscriptionService)
  const router = inject(Router)

  return forkJoin([
    subscriptionService.getSubscriptionDetails(),
    authenticationService.getCurrentUsersRoles$()
  ]).pipe(
    map(([details, roles]) => {
      if (details.status == 'active' || details.status == 'trialing') {
        return true
      } else if (hasUserAdminAccess(...roles)) {
        router.navigate(['billing'])
      } else {
        router.navigate(['error/402'])
      }

      return false
    })
  )
}

/** Checks if the project's subscription is valid */
export const projectIsValidGuard: CanActivateFn = (route, state) => {
  const router = inject(Router)
  const subscriptionService = inject(SubscriptionService)

  const projectID = +route.params.id

  return subscriptionService.getStatusByProject(projectID).pipe(
    catchError(() => router.navigate(['error/404'])),
    map(subscriptionStatus => {
      if (subscriptionStatus == 'active' || subscriptionStatus == 'trialing') {
        return true
      } else {
        router.navigate(['error/402'])
        return false
      }
    })
  )
}

/** Checks if the user has access to edit the project */
export const canEditProjectGuard: CanActivateFn = (route, state) => {
  const router = inject(Router)
  const projectService = inject(ProjectService)
  const authService = inject(AuthenticationService)

  const projectID = route.paramMap.get('id')
  if (!projectID) {
    return of(router.createUrlTree(['/error/403']))
  }

  return projectService.getProjectPermissions(Number(projectID)).pipe(
    map(permissions => {
      if (!permissions || permissions.length === 0) {
        return router.createUrlTree(['/error/403'])
      }
      // Check if user has any valid permission (Owner, Editor)
      const hasAccess = permissions.some(p =>
        p.userID === authService.currentUser?.sub &&
        p.permission &&
        ['Owner', 'Editor'].includes(p.permission)
      )
      if (!hasAccess) {
        return router.createUrlTree(['/error/403'])
      }

      return true
    }),
    catchError(() => of(router.createUrlTree(['/error/404'])))
  )
}

/** Checks if the project is public or if the user has permissions to view it */
export const canViewProjectGuard: CanActivateFn = (route, state) => {
  const router = inject(Router)
  const projectService = inject(ProjectService)
  const authService = inject(AuthenticationService)

  const projectID = route.paramMap.get('id')
  if (!projectID) {
    return of(router.createUrlTree(['/error/403']))
  }

  return projectService.isProjectPublic(Number(projectID)).pipe(
    switchMap(isPublic => {
      if (isPublic) {
        return of(true)
      }

      // Check if it's an organization project
      return projectService.isOrganizationProject(Number(projectID)).pipe(
        switchMap(isOrgProject => {
          if (isOrgProject) {
            return of(true)
          }

          // If not public or organization project, check user permissions
          return projectService.getProjectPermissions(Number(projectID)).pipe(
            map(permissions => {
              if (!permissions || permissions.length === 0) {
                return router.createUrlTree(['/error/403'])
              }

              // Check if user has any valid permission (Owner, Editor, Viewer)
              const hasAccess = permissions.some(p =>
                p.userID === authService.currentUser?.sub &&
                p.permission &&
                ['Owner', 'Editor', 'Viewer'].includes(p.permission)
              )
              if (!hasAccess) {
                return router.createUrlTree(['/error/403'])
              }

              return true
            }),
            catchError(() => of(router.createUrlTree(['/error/404'])))
          )
        })
      )
    }),
    catchError(() => of(router.createUrlTree(['/error/404'])))
  )
}

/** Checks if the authenticated user's subscription is in Pro Tier */
export const isProTierGuard: CanActivateFn = (route, state) => {
  const router = inject(Router)
  const subscriptionService = inject(SubscriptionService)

  return subscriptionService.getSubscriptionDetails().pipe(
    map(details => {
      if (details.tier == 'pro') {
        return true
      } else {
        router.navigate(['projects'])
        return false
      }
    })
  )
}

/** Checks if the authenticated user's subscription is in Free Tier */
export const isFreeTierGuard: CanActivateFn = (route, state) => {
  const router = inject(Router)
  const subscriptionService = inject(SubscriptionService)

  return subscriptionService.getSubscriptionDetails().pipe(
    map(details => {
      if (details.tier == 'free') {
        return true
      } else {
        router.navigate(['billing'])
        return false
      }
    })
  )
}

export const isNotLegacySubscription: CanActivateFn = (route, state) => {
  const subscriptionService = inject(SubscriptionService)
  const router = inject(Router)

  return subscriptionService.getSubscriptionDetails().pipe(
    map(() => {
      if (subscriptionService.inFreeTier) {
        return true
      } else if (subscriptionService.isLegacy) {
        router.navigate(['projects'])
        return false
      } else {
        return true
      }
    }),
    catchError(() => of(true))
  )
}

/** Checks if the authenticated user is a Kartorium super admin (Kartorium staff only, distinct from client "super user" role) */
export const isSuperAdminGuard: CanActivateFn = (route, state) => {
  const userService = inject(UserService)
  const router = inject(Router)

  return userService.getCurrentUser().pipe(
    map(user => {
      if (user?.superAdmin) {
        return true
      } else {
        router.navigate(['projects'])
        return false
      }
    }),
    catchError(() => {
      router.navigate(['projects'])
      return of(false)
    })
  )
}

/**
 * Factory function to create a guard that checks if a specific module is visible for the organization.
 * If hidden, redirects to the org's default home page.
 * Fails open (allows access) if settings can't be fetched.
 */
export function moduleVisibilityGuard(moduleId: string): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const adminService = inject(AdminService)
    const router = inject(Router)

    // If we already have cached settings, use them synchronously
    const cachedSettings = adminService.currentOrgSettings
    if (cachedSettings) {
      const isHidden = cachedSettings.hidden_modules?.includes(moduleId) ?? false
      if (isHidden) {
        const homePage = cachedSettings.default_home_page ?? '/projects'
        router.navigate([homePage])
        return false
      }
      return true
    }

    // Otherwise try to fetch (but fail open on errors)
    return adminService.getCurrentOrgSettings().pipe(
      map(settings => {
        const isHidden = settings?.hidden_modules?.includes(moduleId) ?? false
        if (isHidden) {
          const homePage = settings?.default_home_page ?? '/projects'
          router.navigate([homePage])
          return false
        }
        return true
      }),
      catchError(() => {
        // On error, allow access (fail open)
        return of(true)
      })
    )
  }
}

/** Ordered OEE sub-page definitions: flag key → route */
const OEE_SUB_PAGES: { flag: keyof OeePageFlags; route: string }[] = [
    { flag: 'enable_stations', route: '/oee/stations' },
    { flag: 'enable_analytics', route: '/oee/analytics' },
    { flag: 'enable_process_flow', route: '/oee/process-flow' },
    { flag: 'enable_batch_history', route: '/oee/job-history' },
    { flag: 'enable_jobs', route: '/oee/jobs' },
    { flag: 'enable_labor_tracking', route: '/oee/labor-tracking' },
    { flag: 'enable_product_families', route: '/oee/product-families' },
    { flag: 'enable_operators', route: '/oee/operators' },
]

const OEE_ROLE_MODULE_IDS = [
    'oee',
    'oee_stations',
    'oee_analytics',
    'oee_process_flow',
    'oee_batch_history',
    'oee_products',
    'oee_operators',
]

/**
 * Guard that checks if an OEE sub-page is enabled via feature flags.
 * If the sub-page is disabled, redirects to the first enabled sub-page.
 */
export function oeeSubPageGuard(flagKey: keyof OeePageFlags): CanActivateFn {
    return () => {
        const adminService = inject(AdminService)
        const router = inject(Router)

        return adminService.getCurrentOeePageFlags().pipe(
            map(flags => {
                if (flags[flagKey]) return true

                // Disabled — redirect to first enabled sub-page
                const firstEnabled = OEE_SUB_PAGES.find(p => flags[p.flag])
                if (firstEnabled) {
                    router.navigate([firstEnabled.route])
                } else {
                    // All sub-pages disabled — go home
                    const cachedSettings = adminService.currentOrgSettings
                    router.navigate([cachedSettings?.default_home_page ?? '/projects'])
                }
                return false
            }),
            catchError(() => of(true)) // fail open
        )
    }
}

/**
 * Per-role module visibility. Reads the current user's role settings (visible_modules
 * union) from PermissionService and blocks the route if the module is not allowed.
 *
 * Layered on top of the org-wide moduleVisibilityGuard: this one applies finer
 * per-role restrictions. If a role has no per-role override (visible_modules is null),
 * this guard is a no-op (allows access).
 *
 * Fails open on any backend error so navigation never bricks.
 */
export function roleModuleGuard(moduleId: string): CanActivateFn {
    return () => {
        const permissionService = inject(PermissionService)
        const adminService = inject(AdminService)
        const router = inject(Router)

        const decide = (settings: { visible_modules: string[] | null } | null): boolean => {
            if (settings == null || settings.visible_modules == null) return true
            if (settings.visible_modules.includes(moduleId)) return true
            const home = adminService.currentOrgSettings?.default_home_page ?? '/projects'
            router.navigate([home])
            return false
        }

        // Role assignments can change while the SPA is open. Route decisions must
        // not trust the long-lived sidebar cache as the source of truth.
        return permissionService.refresh().pipe(
            map(s => decide(s)),
            catchError(() => of(true)),
        )
    }
}

export function canAccessOeeShellRole(visibleModules: string[] | null): boolean {
    if (visibleModules === null) return true
    return OEE_ROLE_MODULE_IDS.some(moduleId => visibleModules.includes(moduleId))
}

export const oeeShellRoleGuard: CanActivateFn = () => {
    const permissionService = inject(PermissionService)
    const adminService = inject(AdminService)
    const router = inject(Router)

    const decide = (settings: { visible_modules: string[] | null } | null): boolean => {
        if (settings == null || canAccessOeeShellRole(settings.visible_modules)) return true
        const home = adminService.currentOrgSettings?.default_home_page ?? '/projects'
        router.navigate([home])
        return false
    }

    return permissionService.refresh().pipe(
        map(s => decide(s)),
        catchError(() => of(true)),
    )
}

/**
 * Default-redirect guard for the OEE landing page.
 * Replaces the old static `redirectTo: 'stations'` so users without 'oee_stations'
 * land on the first OEE sub-page they CAN access (analytics → process-flow → job-history),
 * instead of bouncing through a sub-page guard back to /projects.
 */
export const oeeDefaultRouteGuard: CanActivateFn = () => {
    const permissionService = inject(PermissionService)
    const adminService = inject(AdminService)
    const router = inject(Router)

    const decide = (settings: { visible_modules: string[] | null } | null, flags: OeePageFlags) => {
        const visible = settings?.visible_modules ?? null
        const target = pickDefaultOeeSubRoute(visible, flags)
        if (target !== null) return router.parseUrl(target)
        const home = adminService.currentOrgSettings?.default_home_page ?? '/projects'
        return router.parseUrl(home)
    }

    const flags$ = adminService.getCurrentOeePageFlags().pipe(catchError(() => of(adminService.currentOeePageFlags ?? {
        organization_id: '',
        enable_dashboard: true,
        enable_analytics: true,
        enable_process_flow: true,
        enable_batch_history: true,
        enable_stations: true,
        enable_jobs: true,
        enable_labor_tracking: true,
        enable_product_families: true,
        enable_operators: true,
    })))

    return combineLatest([
        permissionService.refresh().pipe(catchError(() => of(null))),
        flags$,
    ]).pipe(
        map(([s, flags]) => decide(s, flags)),
        catchError(() => of(router.parseUrl('/oee/stations'))),
    )
}
